import React, { useEffect, useState, useContext, useRef, useImperativeHandle } from "react";
import { Link, useLocation } from "react-router-dom";

import api from "services/api";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";

import Tooltip from "@mui/material/Tooltip";

import SuiBadge from "components/SuiBadge";
import SuiTypography from "components/SuiTypography";
import SuiBadgeDot from "components/SuiBadgeDot";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import toast from "react-hot-toast";

import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";

import { moodboardSortOptions } from "utils/data";
import { compare } from "utils/helper";
import defaultStyleImage from "assets/images/upstyled/default_background.png";
import Prompt from "components/UpStyled/Prompt";

const ImportMoodboardModal = ({
  visible,
  handleClose,
  handleOk,
  collection,
  selectedMoodboards = [],
  isMulti,
  handleRefresh = () => {},
}) => {
  const [moodboards, setMoodboards] = useState([]);
  const [filteredMoodboards, setFilteredMoodboards] = useState([]);

  const [selectedItems, setSelectedItems] = useState([]);
  const [orderOpt, setOrderOpt] = useState(moodboardSortOptions[0]);
  const [searchStr, setSearchStr] = useState("");
  const [duplicateVisible, setDuplicateVisible] = useState(false);
  const [dupName, setDupName] = useState("");

  useEffect(() => {
    if (visible) {
      retrieveMoodboards();
    }
  }, [visible]);

  const retrieveMoodboards = async () => {
    try {
      const { data } = await api.moodboard.list();

      if (!collection) {
        let myMoodboard = data?.moodboards || [];
        myMoodboard = myMoodboard.sort((a, b) => compare(a, b, "-updated_at"));

        setMoodboards(myMoodboard);
        setFilteredMoodboards(myMoodboard);
        setSelectedItems(selectedMoodboards);
      } else {
        const ids = selectedMoodboards.map((x) => x.moodboard_id ?? x.id);
        let myMoodboard = data?.moodboards.filter((x) => !ids.includes(x.id)) || [];
        myMoodboard = myMoodboard.sort((a, b) => compare(a, b, "-updated_at"));

        setMoodboards(myMoodboard);
        setFilteredMoodboards(myMoodboard);
      }
    } catch (err) {
      toast.error(`Retrieve moodboards failed!`);
      console.log(err);
    }
  };

  const handleImport = async () => {
    if (selectedItems.length == 0) {
      return toast.error(`Please select a moodboard!`);
    }

    try {
      if (collection.id && selectedItems[0].id) {
        if (duplicateVisible) {
          if (!dupName) {
            return toast.error(`Moodboard name is required!`);
          }
          let payload = {
            name: dupName,
            img: selectedItems[0].img,
            sketch: selectedItems[0].sketch,
          };
          const { data } = await api.moodboard.create(payload);

          if (data?.id) {
            await api.moodboard.updateCollectionRelation(data?.id, {
              collection_id: collection.id,
            });
            toast.success(`Duplicate and import moodboard successfully!`);
          }
        } else {
          await api.moodboard.updateCollectionRelation(selectedItems[0].id, {
            collection_id: collection.id,
          });
          toast.success(`Import moodboard successfully!`);
        }

        handleRefresh();
        handleClose();
      } else {
        toast.error(`No related collection!`);
      }
    } catch (err) {
      toast.error(`Import moodboard failed!`);
    }
  };

  const renderPhoto = (props) => {
    const photo = props;
    return (
      <div style={{ padding: "4px" }}>
        <Card
          className="moodboard-wrapper"
          onClick={() => {
            let items = [...selectedItems];
            let findItem = items.find((x) => x.id == photo.id);
            if (findItem?.id) {
              //remove the selected one
              const newItems = items.filter((x) => x.id != photo.id);
              setSelectedItems(newItems);
            } else {
              //add the selected one
              if (isMulti) {
                items.push(photo);
                setSelectedItems(items);
              } else {
                //only keep one selected moodboard
                setSelectedItems([photo]);
              }
            }
          }}
          style={{
            position: "relative",
            cursor: "pointer",
            borderRadius: "16px",
            overflow: "hidden",
            maxHeight: 400,
            width: "100%",
            border: selectedItems.find((x) => x.id == photo.id)
              ? "3px solid rgb(34 211 238)"
              : "none",
          }}
        >
          <img
            style={{ borderRadius: "16px", height: "auto", width: "100%" }}
            alt={photo?.img?.name}
            src={photo?.img?.url || defaultStyleImage}
          />
          <SuiBox className="img-controls">
            <SuiTypography
              variant="body1"
              color="white"
              textTransform="none"
              sx={{ whiteSpace: "nowrap" }}
            >
              Click to select moodboard
            </SuiTypography>
          </SuiBox>
        </Card>

        <SuiBox mt={1} mx={0.5} display="flex" justifyContent="center" alignItems="center">
          <SuiTypography
            variant="h6"
            color="dark"
            textTransform="none"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "1",
              WebkitBoxOrient: "vertical",
            }}
          >
            {photo.name}
          </SuiTypography>
        </SuiBox>
      </div>
    );
  };

  const handleFilterCollections = (str = searchStr) => {
    const copyMoodboards = [...moodboards];
    let filteredList = copyMoodboards; //all projects

    if (str) {
      const strUpper = str.toUpperCase();
      const searchFields = ["name"];
      filteredList = filteredList.filter((x) => {
        let result = false;
        for (let i = 0; i < searchFields.length; i++) {
          if (x[searchFields[i]]) {
            if (typeof x[searchFields[i]] == "string") {
              if (x[searchFields[i]].toUpperCase().includes(strUpper)) {
                result = true;
              }
            } else if (typeof x[searchFields[i]] == "object") {
              const text = JSON.stringify(x[searchFields[i]]);
              if (text.toUpperCase().includes(strUpper)) {
                result = true;
              }
            }
          }
        }
        return result;
      });
    }

    setFilteredMoodboards(filteredList);
  };

  const handleSearchChange = (str) => {
    let formattedStr = "";
    if (str) {
      formattedStr = str.trim();
    }
    setSearchStr(formattedStr);
    handleFilterCollections(formattedStr);
  };

  const handleOnSortChange = (e) => {
    setOrderOpt(e);
    let sortStr = e?.value;
    const newFilteredList = filteredMoodboards.sort((a, b) => compare(a, b, sortStr));
    setFilteredMoodboards(newFilteredList);
  };

  const handleImportConfirm = () => {
    if (handleOk) {
      return handleOk(selectedItems);
    }
    //if already have a collection,we need duplicate the moodboard and then import the new one
    if (selectedItems.length > 0 && selectedItems[0].collection_id) {
      setDuplicateVisible(true);
      const name = "Copy of " + selectedItems[0].name;
      setDupName(name);
    } else {
      handleImport();
    }
  };

  return (
    <Dialog
      open={visible}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="xl"
      fullWidth={true}
    >
      <DialogTitle id="alert-dialog-title">
        Import moodboard{" "}
        <IconButton
          aria-label="close"
          onClick={() => handleClose()}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <SuiBox display="flex" alignItems="center" justifyContent="flex-end" gap={1} mb={1}>
          <SuiBox>
            <Card sx={{ borderRadius: 2, width: 200 }}>
              <SuiSelect
                options={moodboardSortOptions}
                placeholder="Sort"
                isClearable
                onChange={handleOnSortChange}
                value={orderOpt}
              />
            </Card>
          </SuiBox>
          <SuiBox sx={{ maxWidth: 200 }}>
            <SuiInput
              placeholder="Search Keywords"
              type="text"
              onChange={(e) => handleSearchChange(e?.target?.value)}
              icon={{
                direction: "right",
                component: (
                  <SearchIcon
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleSearchChange(searchStr)}
                  />
                ),
              }}
            />
          </SuiBox>
        </SuiBox>

        <SuiBox>
          <div>
            <ResponsiveMasonry
              columnsCountBreakPoints={{ 350: 1, 576: 2, 768: 3, 992: 4, 1200: 4, 1400: 4 }}
            >
              <Masonry>{filteredMoodboards.map((x) => renderPhoto(x))}</Masonry>
            </ResponsiveMasonry>
          </div>
        </SuiBox>
      </DialogContent>
      <DialogActions>
        <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
          Cancel
        </SuiButton>
        <SuiButton size="medium" variant="gradient" color="dark" onClick={handleImportConfirm}>
          Import
        </SuiButton>
      </DialogActions>

      {collection?.id && duplicateVisible && selectedItems.length > 0 ? (
        <Prompt
          heading="Duplicate Moodboard"
          text={
            <div>
              Moodboard:{" "}
              <strong>
                {" "}
                <i>{selectedItems[0].name}</i>
              </strong>{" "}
              already have collections. Do you want to duplicate them and then import it to
              collection:{collection.title}
              <SuiBox>
                <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                  <SuiTypography component="label" variant="caption" fontWeight="bold" required>
                    Moodboard Title
                  </SuiTypography>
                </SuiBox>
                <SuiInput
                  value={dupName}
                  placeholder="Moodboard Title"
                  required
                  onChange={(e) => setDupName(e?.target?.value)}
                />
              </SuiBox>
            </div>
          }
          visible={duplicateVisible}
          okText="Duplicate & Import"
          okColor="dark"
          handleOk={handleOk ? () => handleOk(selectedItems) : handleImport}
          handleClose={() => setDuplicateVisible(false)}
        />
      ) : null}
    </Dialog>
  );
};

export default ImportMoodboardModal;
